/**
 * @file AuthContext.js
 * @brief Context de React para gestión de autenticación JWT
 */

import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from '../config/axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Verificar si hay token guardado (localStorage o sessionStorage)
    const token = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
    const userData = localStorage.getItem('user') || sessionStorage.getItem('user');
    
    if (token && userData && userData !== 'undefined' && userData !== 'null') {
      try {
        setUser(JSON.parse(userData));
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      } catch (error) {
        console.error('Error parsing user data:', error);
        // Limpiar datos corruptos
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        sessionStorage.removeItem('accessToken');
        sessionStorage.removeItem('refreshToken');
        sessionStorage.removeItem('user');
      }
    }
    
    setLoading(false);
  }, []);

  const login = async (email, password, rememberMe = true) => {
    try {
      const response = await axios.post('/api/auth/login', { email, password });
      const { accessToken, refreshToken, usuario } = response.data;
      
      if (!accessToken || !refreshToken || !usuario) {
        throw new Error('Respuesta del servidor incompleta');
      }
      
      // Guardar en localStorage (persistente) o sessionStorage (temporal)
      const storage = rememberMe ? localStorage : sessionStorage;
      storage.setItem('accessToken', accessToken);
      storage.setItem('refreshToken', refreshToken);
      storage.setItem('user', JSON.stringify(usuario));
      
      // También guardar en localStorage como respaldo
      if (!rememberMe) {
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', refreshToken);
        localStorage.setItem('user', JSON.stringify(usuario));
      }
      
      axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
      
      setUser(usuario);
      return { success: true };
    } catch (error) {
      console.error('Error en login:', error);
      return { 
        success: false, 
        message: error.response?.data?.error || 'Error al iniciar sesión'
      };
    }
  };

  const logout = () => {
    // Limpiar ambos storages
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    sessionStorage.removeItem('accessToken');
    sessionStorage.removeItem('refreshToken');
    sessionStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  const refreshToken = async () => {
    try {
      const refresh = localStorage.getItem('refreshToken') || sessionStorage.getItem('refreshToken');
      if (!refresh) throw new Error('No refresh token');
      
      const response = await axios.post('/api/auth/refresh', { refreshToken: refresh });
      const { accessToken } = response.data;
      
      // Actualizar en ambos storages si existen
      if (localStorage.getItem('accessToken')) {
        localStorage.setItem('accessToken', accessToken);
      }
      if (sessionStorage.getItem('accessToken')) {
        sessionStorage.setItem('accessToken', accessToken);
      }
      
      axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
      
      return accessToken;
    } catch (error) {
      logout();
      throw error;
    }
  };

  // Interceptor para renovar token automáticamente
  useEffect(() => {
    const doRefreshToken = async () => {
      try {
        const refresh = localStorage.getItem('refreshToken') || sessionStorage.getItem('refreshToken');
        if (!refresh) throw new Error('No refresh token');
        
        const response = await axios.post('/api/auth/refresh', { refreshToken: refresh });
        const { accessToken } = response.data;
        
        // Actualizar en ambos storages si existen
        if (localStorage.getItem('accessToken')) {
          localStorage.setItem('accessToken', accessToken);
        }
        if (sessionStorage.getItem('accessToken')) {
          sessionStorage.setItem('accessToken', accessToken);
        }
        
        axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
        
        return accessToken;
      } catch (error) {
        logout();
        throw error;
      }
    };

    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        
        // Manejar 401 (Unauthorized) y 403 (Forbidden) con token expirado
        if ((error.response?.status === 401 || error.response?.status === 403) && !originalRequest._retry) {
          originalRequest._retry = true;
          
          try {
            const token = await doRefreshToken();
            originalRequest.headers['Authorization'] = `Bearer ${token}`;
            return axios(originalRequest);
          } catch (refreshError) {
            return Promise.reject(refreshError);
          }
        }
        
        return Promise.reject(error);
      }
    );
    
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = {
    user,
    loading,
    login,
    logout,
    refreshToken,
    isAuthenticated: !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
